import React, { useState } from 'react';
import TopBar from '../components/layout/TopBar';
import {
  GlassCard, Button, InputField, SectionHeader, Divider, Badge,
} from '../components/ui';
import { useToast } from '../context/ToastContext';

const Settings: React.FC = () => {
  const { addToast } = useToast();

  // Profile state
  const [profile, setProfile] = useState({
    name: 'Admin User',
    email: 'admin@vop.com',
    phone: '+63 917 000 0000',
    role: 'ADMIN',
  });

  // Password state
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });

  // App config
  const [config, setConfig] = useState({
    companyName: 'Valley of Peace Memorial Park',
    defaultCurrency: 'PHP',
    defaultInstallmentMonths: '12',
    reservationFeePercent: '20',
    autoAssignAgent: true,
    emailNotifications: true,
    smsNotifications: false,
    overdueAlertDays: '7',
  });

  // Notification prefs
  const [notifications, setNotifications] = useState({
    newSale: true,
    paymentReceived: true,
    paymentOverdue: true,
    newClient: true,
    stageChange: false,
    dailyDigest: true,
  });

  const handleSaveProfile = () => {
    if (!profile.name || !profile.email) {
      addToast('error', 'Validation Error', 'Name and email are required.');
      return;
    }
    addToast('success', 'Profile Updated', 'Your profile has been saved.');
  };

  const handleChangePassword = () => {
    if (!passwords.current || !passwords.newPass || !passwords.confirm) {
      addToast('error', 'Validation Error', 'All password fields are required.');
      return;
    }
    if (passwords.newPass !== passwords.confirm) {
      addToast('error', 'Mismatch', 'New password and confirmation do not match.');
      return;
    }
    if (passwords.newPass.length < 8) {
      addToast('error', 'Too Short', 'Password must be at least 8 characters.');
      return;
    }
    setPasswords({ current: '', newPass: '', confirm: '' });
    addToast('success', 'Password Changed', 'Your password has been updated.');
  };

  const handleSaveConfig = () => {
    addToast('success', 'Settings Saved', 'Application configuration has been updated.');
  };

  const handleSaveNotifications = () => {
    addToast('success', 'Preferences Saved', 'Notification preferences updated.');
  };

  const toggleStyle: React.CSSProperties = {
    width: '44px', height: '24px', borderRadius: 'var(--radius-full)',
    position: 'relative', cursor: 'pointer', transition: 'background 0.2s ease',
    border: 'none', padding: 0, flexShrink: 0,
  };

  const toggleKnobStyle = (on: boolean): React.CSSProperties => ({
    width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
    position: 'absolute', top: '3px', transition: 'left 0.2s ease',
    left: on ? '23px' : '3px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  });

  const Toggle = ({ value, onChange }: { value: boolean; onChange: () => void }) => (
    <button onClick={onChange} style={{ ...toggleStyle, background: value ? 'var(--primary-600)' : 'rgba(148,163,184,0.20)' }}>
      <span style={toggleKnobStyle(value)} />
    </button>
  );

  const SettingRow = ({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
      <div>
        <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 500 }}>{label}</div>
        {description && <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>{description}</div>}
      </div>
      {children}
    </div>
  );

  return (
    <>
      <TopBar title="Settings" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '800px' }}>

        {/* Profile */}
        <GlassCard>
          <SectionHeader title="Profile" icon="solar:user-id-bold-duotone"
            actions={<Badge variant="info">{profile.role}</Badge>} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <InputField label="Full Name" value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              icon="solar:user-bold-duotone" />
            <InputField label="Email" type="email" value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              icon="solar:letter-bold-duotone" />
            <InputField label="Phone" value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              icon="solar:phone-bold-duotone" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Role</label>
              <div style={{
                background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--text-secondary)',
                fontSize: '14px',
              }}>{profile.role} (read-only)</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveProfile}>Save Profile</Button>
          </div>
        </GlassCard>

        {/* Change Password */}
        <GlassCard>
          <SectionHeader title="Change Password" icon="solar:lock-bold-duotone" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <InputField label="Current Password" type="password" value={passwords.current}
              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} />
            <InputField label="New Password" type="password" value={passwords.newPass}
              onChange={(e) => setPasswords({ ...passwords, newPass: e.target.value })} />
            <InputField label="Confirm Password" type="password" value={passwords.confirm}
              onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button variant="secondary" icon="solar:lock-bold-duotone" onClick={handleChangePassword}>Change Password</Button>
          </div>
        </GlassCard>

        {/* Application Configuration */}
        <GlassCard>
          <SectionHeader title="Application Configuration" icon="solar:settings-bold-duotone" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
            <InputField label="Company Name" value={config.companyName}
              onChange={(e) => setConfig({ ...config, companyName: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Currency</label>
              <select value={config.defaultCurrency} onChange={(e) => setConfig({ ...config, defaultCurrency: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                <option value="PHP">PHP (₱)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
            <InputField label="Default Installment Months" type="number" value={config.defaultInstallmentMonths}
              onChange={(e) => setConfig({ ...config, defaultInstallmentMonths: e.target.value })} />
            <InputField label="Reservation Fee (%)" type="number" value={config.reservationFeePercent}
              onChange={(e) => setConfig({ ...config, reservationFeePercent: e.target.value })} />
            <InputField label="Overdue Alert (days)" type="number" value={config.overdueAlertDays}
              onChange={(e) => setConfig({ ...config, overdueAlertDays: e.target.value })} />
          </div>
          <Divider spacing="16px" />
          <SettingRow label="Auto-assign Agent" description="Automatically assign new clients to available agents">
            <Toggle value={config.autoAssignAgent} onChange={() => setConfig({ ...config, autoAssignAgent: !config.autoAssignAgent })} />
          </SettingRow>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveConfig}>Save Configuration</Button>
          </div>
        </GlassCard>

        {/* Notification Preferences */}
        <GlassCard>
          <SectionHeader title="Notification Preferences" icon="solar:notification-unread-bold-duotone" />
          <div style={{ marginTop: '8px' }}>
            <SettingRow label="Email Notifications" description="Receive email alerts">
              <Toggle value={config.emailNotifications} onChange={() => setConfig({ ...config, emailNotifications: !config.emailNotifications })} />
            </SettingRow>
            <SettingRow label="SMS Notifications" description="Receive SMS alerts">
              <Toggle value={config.smsNotifications} onChange={() => setConfig({ ...config, smsNotifications: !config.smsNotifications })} />
            </SettingRow>
            <Divider spacing="8px" />
            <SettingRow label="New Sale" description="When a new sale is created">
              <Toggle value={notifications.newSale} onChange={() => setNotifications({ ...notifications, newSale: !notifications.newSale })} />
            </SettingRow>
            <SettingRow label="Payment Received" description="When a payment is recorded">
              <Toggle value={notifications.paymentReceived} onChange={() => setNotifications({ ...notifications, paymentReceived: !notifications.paymentReceived })} />
            </SettingRow>
            <SettingRow label="Payment Overdue" description="When a payment becomes overdue">
              <Toggle value={notifications.paymentOverdue} onChange={() => setNotifications({ ...notifications, paymentOverdue: !notifications.paymentOverdue })} />
            </SettingRow>
            <SettingRow label="New Client" description="When a new client is added">
              <Toggle value={notifications.newClient} onChange={() => setNotifications({ ...notifications, newClient: !notifications.newClient })} />
            </SettingRow>
            <SettingRow label="Stage Changes" description="When a sale moves to a new stage">
              <Toggle value={notifications.stageChange} onChange={() => setNotifications({ ...notifications, stageChange: !notifications.stageChange })} />
            </SettingRow>
            <SettingRow label="Daily Digest" description="Receive a daily summary email">
              <Toggle value={notifications.dailyDigest} onChange={() => setNotifications({ ...notifications, dailyDigest: !notifications.dailyDigest })} />
            </SettingRow>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveNotifications}>Save Preferences</Button>
          </div>
        </GlassCard>

        {/* Danger Zone */}
        <GlassCard style={{ borderColor: 'rgba(239,68,68,0.20)' }}>
          <SectionHeader title="Danger Zone" icon="solar:danger-triangle-bold-duotone" />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
            <div>
              <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 500 }}>Delete Account</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>Permanently delete your account and all associated data</div>
            </div>
            <Button variant="danger" size="sm" icon="solar:trash-bin-trash-bold-duotone"
              onClick={() => addToast('error', 'Not Implemented', 'Account deletion requires admin approval.')}>
              Delete Account
            </Button>
          </div>
        </GlassCard>
      </div>
    </>
  );
};

export default Settings;
